import {useResizeObserver} from '@mantine/hooks';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {
  REDESIGN_DRAWER_MAX_WIDTH_RATIO,
  REDESIGN_DRAWER_WIDTH_PX,
  REDESIGN_DRAWER_WIDTH_STORAGE_KEY,
} from 'llm/chat-library/ui/AIStyleConstants';
import LocalStorage from 'LocalStorage';

/** The drawer can never be dragged narrower than its default (un-resized) width. */
const MIN_WIDTH = REDESIGN_DRAWER_WIDTH_PX;

function useDocumentListener<K extends keyof DocumentEventMap>(
  event: K,
  handler: ((e: DocumentEventMap[K]) => void) | null
): void {
  useEffect(() => {
    if (handler == null) {
      return;
    }
    document.addEventListener(event, handler);
    return () => {
      document.removeEventListener(event, handler);
    };
  }, [event, handler]);
}

/**
 * Clamps a candidate drawer width into `[MIN_WIDTH, maxWidth]`. The minimum is applied last so it
 * wins when the container is too narrow to honor both bounds (better to overflow slightly than to
 * collapse below the width the sidebar content needs).
 */
function clampWidth(width: number, maxWidth: number): number {
  return Math.max(MIN_WIDTH, Math.min(maxWidth, width));
}

function readStoredWidth(): number | null {
  const stored = LocalStorage.getItem(REDESIGN_DRAWER_WIDTH_STORAGE_KEY);
  if (stored == null) {
    return null;
  }
  const parsed = parseInt(stored, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

interface UseResizableDrawerResult {
  /** Current drawer width in pixels, clamped to the allowed range. */
  width: number;
  isResizing: boolean;
  /** Attach to the drag handle's `onMouseDown`. */
  handleMouseDown: (e: React.MouseEvent) => void;
  /** Attach to the layout container whose width bounds the drawer's max. */
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
}

/**
 * Drag-to-resize state for the redesigned chat left drawer (a left-docked column). Dragging the
 * handle right widens the drawer; the width is clamped between its default width and a fraction of
 * the container width, and persisted to LocalStorage so it sticks across sessions.
 *
 * Mouse move/up are bound to the document (not the handle) so a fast drag that leaves the panel
 * keeps tracking and always releases cleanly.
 */
export default function useResizableDrawer({enabled}: {enabled: boolean}): UseResizableDrawerResult {
  const [containerRef, containerRect] = useResizeObserver<HTMLDivElement>();
  const [width, setWidth] = useState<number>(() => clampWidth(readStoredWidth() ?? MIN_WIDTH, Infinity));
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const getMaxWidth = useCallback(
    (): number => (containerRect.width || window.innerWidth) * REDESIGN_DRAWER_MAX_WIDTH_RATIO,
    [containerRect.width]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      // Left-docked: dragging the handle right (increasing clientX) widens the drawer.
      const delta = e.clientX - startXRef.current;
      setWidth(clampWidth(startWidthRef.current + delta, getMaxWidth()));
    },
    [getMaxWidth]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useDocumentListener('mousemove', isResizing ? handleMouseMove : null);
  useDocumentListener('mouseup', isResizing ? handleMouseUp : null);

  // Reset the global cursor/selection overrides if the component unmounts mid-drag.
  useEffect(() => {
    if (!isResizing) {
      return;
    }
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Keep the width within the max as the container shrinks (also clamps a stored width on mount).
  useEffect(() => {
    if (!enabled) {
      return;
    }
    setWidth((prev) => clampWidth(prev, getMaxWidth()));
  }, [enabled, getMaxWidth]);

  // Persist the chosen width once the drag settles (skip while actively dragging).
  useEffect(() => {
    if (!enabled || isResizing) {
      return;
    }
    LocalStorage.setItem(REDESIGN_DRAWER_WIDTH_STORAGE_KEY, String(width));
  }, [width, enabled, isResizing]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled) {
        return;
      }
      e.preventDefault();
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      setIsResizing(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [enabled, width]
  );

  return {width, isResizing, handleMouseDown, containerRef};
}
